import React, { useState, useMemo } from 'react';
import {
  Box, Typography, Paper, TextField, InputAdornment, List, ListItem,
  ListItemText, ListItemSecondaryAction, Switch, IconButton, Button,
  Chip, Snackbar, Alert, Skeleton, CircularProgress,
} from '@mui/material';
import { Add, Delete, Kitchen, Search } from '@mui/icons-material';
import { useRecipes } from '../../contexts/RecipeContext';

export default function PantryPage() {
  const { pantry, pantryLoading, updatePantry } = useRecipes();
  const [newItem, setNewItem] = useState('');
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const items = useMemo(() => pantry?.items || [], [pantry]);

  const filteredItems = useMemo(() => {
    if (!search) return items;
    return items.filter((item) => item.name.toLowerCase().includes(search.toLowerCase()));
  }, [items, search]);

  const inStockItems = filteredItems.filter((i) => i.inStock);
  const outOfStockItems = filteredItems.filter((i) => !i.inStock);

  const save = async (newItems) => {
    setSaving(true);
    try {
      await updatePantry(newItems);
    } catch (err) {
      showSnackbar('Failed to save pantry', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleAddItem = async () => {
    const name = newItem.trim();
    if (!name) return;
    if (items.some((i) => i.name.toLowerCase() === name.toLowerCase())) {
      showSnackbar('Item already in pantry', 'warning');
      return;
    }
    const newItems = [
      ...items,
      { id: Date.now().toString(), name, inStock: true },
    ];
    setNewItem('');
    await save(newItems);
    showSnackbar(`"${name}" added`);
  };

  const handleToggle = async (id) => {
    const newItems = items.map((i) => i.id === id ? { ...i, inStock: !i.inStock } : i);
    await save(newItems);
  };

  const handleDelete = async (id) => {
    const newItems = items.filter((i) => i.id !== id);
    await save(newItems);
  };

  const handleMarkAllInStock = async () => {
    const newItems = items.map((i) => ({ ...i, inStock: true }));
    await save(newItems);
    showSnackbar('All items marked as in stock');
  };

  const handleClearAll = async () => {
    await save([]);
    showSnackbar('Pantry cleared');
  };

  const ItemRow = ({ item }) => (
    <ListItem
      sx={{
        borderRadius: 2,
        mb: 0.5,
        bgcolor: 'rgba(255,255,255,0.03)',
        border: '1px solid rgba(255,255,255,0.05)',
        transition: 'opacity 0.2s',
        opacity: item.inStock ? 1 : 0.6,
      }}
    >
      <Switch
        checked={item.inStock}
        onChange={() => handleToggle(item.id)}
        color="success"
        size="small"
        sx={{ mr: 1.5 }}
      />
      <ListItemText
        primary={item.name}
        primaryTypographyProps={{
          fontWeight: item.inStock ? 500 : 400,
          color: item.inStock ? 'text.primary' : 'text.secondary',
        }}
      />
      <ListItemSecondaryAction>
        <IconButton size="small" onClick={() => handleDelete(item.id)} sx={{ color: 'error.main', opacity: 0.7 }}>
          <Delete fontSize="small" />
        </IconButton>
      </ListItemSecondaryAction>
    </ListItem>
  );

  if (pantryLoading) {
    return (
      <Box sx={{ p: 3 }}>
        <Skeleton variant="text" width={200} height={40} />
        <Skeleton variant="rounded" height={56} sx={{ mt: 2 }} />
        {[1, 2, 3, 4].map((i) => <Skeleton key={i} variant="rounded" height={56} sx={{ mt: 1 }} />)}
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 700, mx: 'auto', p: { xs: 2, md: 3 } }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box>
          <Typography variant="h5">My Pantry</Typography>
          <Typography variant="body2" color="text.secondary">
            {inStockItems.length} in stock · {items.length} total
          </Typography>
        </Box>
        {saving && <CircularProgress size={20} sx={{ color: 'primary.main' }} />}
      </Box>

      {/* Add item */}
      <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
        <TextField
          placeholder="Add ingredient (e.g. olive oil, garlic)..."
          value={newItem}
          onChange={(e) => setNewItem(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleAddItem()}
          fullWidth
          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Kitchen sx={{ color: 'text.secondary' }} />
              </InputAdornment>
            ),
          }}
        />
        <Button
          variant="contained"
          onClick={handleAddItem}
          disabled={!newItem.trim()}
          sx={{ flexShrink: 0, background: 'linear-gradient(135deg, #f0873c, #c96820)' }}
        >
          <Add />
        </Button>
      </Box>

      {/* Search */}
      {items.length > 5 && (
        <TextField
          placeholder="Search pantry..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          fullWidth
          size="small"
          sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ fontSize: 18, color: 'text.secondary' }} />
              </InputAdornment>
            ),
          }}
        />
      )}

      {/* Bulk actions */}
      {items.length > 0 && (
        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
          <Button size="small" onClick={handleMarkAllInStock} sx={{ color: 'success.main' }}>
            Mark all in stock
          </Button>
          <Button size="small" onClick={handleClearAll} sx={{ color: 'error.main' }}>
            Clear all
          </Button>
        </Box>
      )}

      {items.length === 0 ? (
        <Paper sx={{ p: 6, textAlign: 'center' }}>
          <Typography sx={{ fontSize: '3rem', mb: 2 }}>🧺</Typography>
          <Typography variant="h6" sx={{ mb: 1 }}>Your pantry is empty</Typography>
          <Typography color="text.secondary">
            Add ingredients you have at home to see what you can cook.
          </Typography>
        </Paper>
      ) : (
        <>
          {/* In Stock */}
          {inStockItems.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography variant="subtitle2" color="success.main">In Stock</Typography>
                <Chip
                  label={inStockItems.length}
                  size="small"
                  sx={{ bgcolor: 'rgba(102,187,106,0.15)', color: 'success.main', height: 20, fontSize: '0.7rem' }}
                />
              </Box>
              <List dense disablePadding>
                {inStockItems.map((item) => <ItemRow key={item.id} item={item} />)}
              </List>
            </Box>
          )}

          {/* Out of Stock */}
          {outOfStockItems.length > 0 && (
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography variant="subtitle2" color="text.secondary">Out of Stock</Typography>
                <Chip
                  label={outOfStockItems.length}
                  size="small"
                  sx={{ bgcolor: 'rgba(255,255,255,0.05)', height: 20, fontSize: '0.7rem' }}
                />
              </Box>
              <List dense disablePadding>
                {outOfStockItems.map((item) => <ItemRow key={item.id} item={item} />)}
              </List>
            </Box>
          )}
        </>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((p) => ({ ...p, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
